//////////////////
// IMPORTS
//////////////////
// schema
import * as ISchema from "@schema";
// libraries
import * as React from "react";
import { connect } from "react-redux";
import { createRefetchContainer } from "react-relay";
import moment from "moment-timezone";
import { isEqual } from "lodash";
// chrome-create-event imports
import { IProps, IContainer, IState } from "./ChromeCreateEvent.d";
import * as s from "./ChromeCreateEvent.styles";
import { chromeCreateEventRendererQuery } from "./ChromeCreateEvent.gql";
// redux actions
import { snackBarOpen } from "@actions/snack-bar.actions";
// other internals
import { EventSchedulerTimeInput } from "@components/event-scheduler-time-input";
import { RoomOptionEnum } from "@components/event-scheduler/EventScheduler.d";
import { fragments } from "./ChromeCreateEvent.gql";
// state
import { IReduxState } from "@state/reducers/root.reducer";
import { IEditEventInfo } from "@reducers/chrome-extension.reducer";
// constants
// util
import {
  convertDataDateTimeToMoment,
  constructDataTime,
  constructDataDate,
} from "@util/google-edit-page.util";
import { getRenderTimeZone } from "@util/time-zone.util";
import { EScheduleRange, getMaxMinSlotForRange, slotsEqual } from "@util/scheduler.util";
import { EFlex, EAutopilotWarning } from "@util/event-tag.util";
import { PostMessageManager } from "@util/post-message.util";
import { track, TrackingEvents } from "@util/analytics.util";
// material-ui
import { Divider } from "@material-ui/core";

//////////////////
// COMPONENT
//////////////////
class ChromeCreateEventComponent extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);
    const { editEventInfo, org } = props;
    const { start, duration } = this.getMomentsAndDurationFromDataDateTime(editEventInfo);

    this.state = {
      calendarIds: props.editEventInfo.attendees,
      minutesDuration: duration,
      includeRoom:
        props.editEventInfo.rooms?.length > 0
          ? RoomOptionEnum.SpecificRoom
          : RoomOptionEnum.WithoutRoom,
      range: EScheduleRange.AroundDate,
      specifiedDate: start.toDate(),
      roomIds: props.editEventInfo.rooms || [],
      selectedSlot: undefined,
      timeError: "",
      uncrawledCalendarsCount: 0,
      flex: EFlex.Pin,
      allowedStart: null,
      allowedEnd: null,
      ignoreEventInfoChange: false,
    };

    this.props.relay.refetch({
      calendarIds: [org.primaryOrgCalendar, ...(props?.editEventInfo?.attendees || [])],
      orgRelayId: org.id,
    });
  }

  public UNSAFE_componentWillReceiveProps(nextProps: IProps) {
    if (this.state.ignoreEventInfoChange) {
      return;
    }
    const { start, duration } = this.getMomentsAndDurationFromDataDateTime(nextProps.editEventInfo);

    const includeRoom =
      nextProps.editEventInfo.rooms?.length > 0
        ? RoomOptionEnum.SpecificRoom
        : this.state.includeRoom === RoomOptionEnum.SpecificRoom
        ? RoomOptionEnum.WithRoom
        : this.state.includeRoom;

    const nextState = {
      includeRoom,
      calendarIds: nextProps.editEventInfo.attendees,
      minutesDuration: duration,
      range: EScheduleRange.AroundDate,
      specifiedDate: start.toDate(),
      roomIds: nextProps.editEventInfo.rooms || [],
    };
    this.setState({ ...nextState });

    if (!isEqual(nextProps.editEventInfo.attendees, this.state.calendarIds)) {
      this.props.relay.refetch({
        calendarIds: [
          this.props.org.primaryOrgCalendar,
          ...(nextProps?.editEventInfo?.attendees || []),
        ],
        orgRelayId: this.props.org.id,
      });
    }
  }

  // ~-~-~-~-~-~-~-
  // Helpers
  // ~-~-~-~-~-~-~-
  private getMomentsAndDurationFromDataDateTime = (editEventInfo: IEditEventInfo) => {
    const { startDate, startTime, endDate, endTime } = editEventInfo;

    let duration = 60;
    let start = moment();
    if (startDate && startTime && endDate && endTime) {
      start = convertDataDateTimeToMoment(startDate, startTime);
      const end = convertDataDateTimeToMoment(endDate, endTime);
      duration = Math.round(end.diff(start) / 60000);
    }

    return { start, duration };
  };

  private maybeGetWarning = () => {
    const { org, editEventInfo } = this.props;
    const isExternal = editEventInfo.attendees.some(
      (attendee) => org.domains.indexOf(attendee.split("@")[1]) === -1,
    );
    const isAllDay = editEventInfo.allDayEvent;

    if (isAllDay) {
      return EAutopilotWarning.AllDayEvent;
    } else if (isExternal) {
      return EAutopilotWarning.ExternalAttendee;
    }

    return undefined;
  };

  private onTimeChange = (
    minutesDuration: number,
    includeRoom: RoomOptionEnum,
    range: EScheduleRange,
    specifiedDate: Date | null,
    uncrawledCalendarsCount: number,
    selectedSlot?: ISchema.ISchedulingSlot,
  ) => {
    if (this.state.ignoreEventInfoChange) {
      return; // Wait for Google UI to update to avoid trying to set the UI while a previous set is underway.
    }

    this.setState({
      minutesDuration,
      includeRoom,
      range,
      specifiedDate,
      uncrawledCalendarsCount,
      selectedSlot,
    });

    if (!selectedSlot || slotsEqual(selectedSlot, this.state.selectedSlot)) {
      return;
    }

    const start = moment.tz(
      selectedSlot.timeRange.startTime.dateOrDateTime,
      "YYYY-MM-DD HH:mma",
      getRenderTimeZone(),
    );
    const end = moment.tz(
      selectedSlot.timeRange.endTime.dateOrDateTime,
      "YYYY-MM-DD HH:mma",
      getRenderTimeZone(),
    );

    const eventInfo = {
      startDate: constructDataDate(start.toDate()),
      startTime: constructDataTime(start.toDate(), true),
      endTime: constructDataTime(end.toDate(), true),
      endDate: constructDataDate(end.toDate()),
      room:
        selectedSlot.summary.availableRooms.length > 0
          ? selectedSlot.summary.availableRooms[0]
          : null,
    };

    const { duration } = this.getMomentsAndDurationFromDataDateTime(this.props.editEventInfo);
    if (duration !== minutesDuration) {
      // gonna have to call this twice if the duration is different because chrome is weird..
      PostMessageManager.sendUpdatedEditEventInfo(eventInfo);
    }
    PostMessageManager.sendUpdatedEditEventInfo(eventInfo);
    this.props.snackBarOpen("Success! Set your event info.");

    // Ignore changes to date and time while the chrome extension updates the Google UI.
    this.setState({ ignoreEventInfoChange: true });
    setTimeout(() => this.setState({ ignoreEventInfoChange: false }), 1200);

    track(TrackingEvents.SCHEDULING.GCAL_CREATE_EVENT_TIME_SELECTED, {
      startDate: moment(eventInfo.startDate).format("MMM D, YYYY"),
      startTime: constructDataTime(start.toDate(), true),
      endTime: constructDataTime(end.toDate(), true),
      endDate: moment(eventInfo.endDate).format("MMM D, YYYY"),
      room:
        selectedSlot.summary.availableRooms.length > 0
          ? selectedSlot.summary.availableRooms[0]
          : null,
    });
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  public render() {
    const { org, editEventInfo, relay } = this.props;

    const now = new Date();
    const FOUR_HOUR_MAX = 240;
    const warning = this.maybeGetWarning();

    if (warning === EAutopilotWarning.AllDayEvent) {
      return <div style={s.warning}>Scheduling meetings does not support all day events.</div>;
    } else if (this.state.specifiedDate && this.state.specifiedDate < now) {
      return <div style={s.warning}>Meetings in the past aren't supported.</div>;
    } else if (this.state.minutesDuration > FOUR_HOUR_MAX) {
      return <div style={s.warning}>Meetings over 4 hours aren't supported.</div>;
    } else if (this.state.minutesDuration < 1) {
      return <div style={s.warning}>Meetings under 1 minute aren't supported.</div>;
    } else if (this.state.calendarIds.length > 15) {
      return <div style={s.warning}>Meetings with over 15 attendees aren't supported.</div>;
    }

    const specifiedMoment = this.state.specifiedDate ? moment(this.state.specifiedDate) : undefined;
    const { rangeMin, rangeMax } = getMaxMinSlotForRange(this.state.range, specifiedMoment);

    const invitees = this.state.calendarIds.map((calendarId) => ({ calendarId, required: true }));
    // If the query has no attendees, or single attendee of just the current user, declare this as a TaskHold.
    const eventTraits = [];
    if (invitees.filter((invitee) => invitee.calendarId !== org.primaryOrgCalendar).length === 0) {
      eventTraits.push(ISchema.EventTrait.TaskHold);
    }

    const schedulerQueryInput = {
      orgId: org.id,
      environment: relay.environment,
      invitees: invitees,
      minutesDuration: this.state.minutesDuration,
      timeZone: editEventInfo.startTimeZone || getRenderTimeZone(),
      minSlot: rangeMin,
      maxSlot: rangeMax,
      includeRoom: this.state.includeRoom,
      roomsOverride: this.state.includeRoom ? this.state.roomIds : [],
      limit: 10,
      seenSlots: [],
      traits: eventTraits,
    };

    const persons =
      (org?.personsByCalendarIdsErrorable?.__typename !== "GraphEntityError" &&
        org.personsByCalendarIdsErrorable?.list) ||
      [];

    return (
      <div>
        <EventSchedulerTimeInput
          {...schedulerQueryInput}
          range={this.state.range}
          specifiedDate={this.state.specifiedDate}
          selectedSlot={this.state.selectedSlot}
          onChange={this.onTimeChange}
          open={true}
          persons={persons}
          useAutoSchedule={false}
          isLoading={this.state.ignoreEventInfoChange}
          showCloseButton={false}
        />
        <Divider style={{ flexShrink: 0, marginTop: 15, marginBottom: 15 }} />
      </div>
    );
  }
}

//////////////////
// REDUX
//////////////////
function mapStateToProps(state: IReduxState, _ownProps: IContainer) {
  return {
    editEventInfo: state.webExtension.editEventInfo,
  };
}

const reduxContainer = connect(mapStateToProps, {
  snackBarOpen,
})(ChromeCreateEventComponent);

//////////////////
// RELAY
//////////////////
export const ChromeCreateEvent = createRefetchContainer(
  reduxContainer,
  fragments,
  chromeCreateEventRendererQuery,
);
